import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_workflow-builder-125/artifacts/y4lualkn_Zelaflo%20Logo.svg";

const links = [
  { to: "/", label: "Home" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? (theme === "dark" ? "bg-[#171412]/75 backdrop-blur-xl border-b border-white/5" : "bg-white/75 backdrop-blur-xl border-b border-[#6B2FD9]/10")
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-[70px]">
        <Link to="/" className="flex items-center" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Zelaflo Labs" className="h-9 w-auto" draggable={false} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-[color:var(--text)]" : "opacity-60 hover:opacity-100"
                }`
              }
              style={{ color: "var(--text)" }}
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-3 right-3 -bottom-0.5 h-[2px] bg-[#6B2FD9] rounded-full"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={toggle}
            className="w-10 h-10 rounded-full border flex items-center justify-center transition-all hover:scale-110"
            style={{ borderColor: "var(--border-strong)", color: "var(--text)" }}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={theme}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="flex"
              >
                {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
              </motion.span>
            </AnimatePresence>
          </button>
          <button
            onClick={() => navigate("/contact")}
            className="btn-primary-zl px-5 py-2.5 rounded-full text-sm font-semibold"
            data-testid="nav-get-started-btn"
          >
            Get Started
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <button
            onClick={toggle}
            className="w-9 h-9 rounded-full border flex items-center justify-center"
            style={{ borderColor: "var(--border-strong)", color: "var(--text)" }}
            data-testid="theme-toggle-mobile"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <button
            className="p-2"
            onClick={() => setOpen((v) => !v)}
            style={{ color: "var(--text)" }}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={`lg:hidden overflow-hidden backdrop-blur-xl border-t ${theme === "dark" ? "bg-[#171412]/95 border-white/5" : "bg-white/95 border-[#6B2FD9]/10"}`}
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-lg text-base ${isActive ? "bg-white/5" : "opacity-70 hover:opacity-100"}`
                  }
                  style={{ color: "var(--text)" }}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                onClick={() => { setOpen(false); navigate("/contact"); }}
                className="btn-primary-zl mt-3 px-5 py-3 rounded-full text-sm font-semibold"
                data-testid="mobile-get-started-btn"
              >
                Get Started
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
